import { useState, useCallback } from "react";
import api from "../utils/api";
import { sensorLage, windColor } from "../utils/wind";

const SENSOR_COLORS = [
  "#22c55e","#3b82f6","#f97316","#a855f7","#eab308",
  "#ef4444","#06b6d4","#f43f5e","#84cc16","#0ea5e9",
];

function fmt(v, d = 1) {
  if (v === null || v === undefined) return "–";
  return Number(v).toFixed(d);
}

function fmtMin(min) {
  if (!min) return "0 Min.";
  if (min < 60) return `${Math.round(min)} Min.`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

// ── Mini Status-Zeitreihe (SVG) ────────────────────────────────
function StatusTimeline({ chart, color }) {
  if (!chart || chart.length < 2) return null;
  const W = 300, H = 24;
  const n = chart.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, display: "block" }}>
      {chart.map((pt, i) => {
        if (i === 0) return null;
        const x1 = ((i - 1) / (n - 1)) * W;
        const x2 = (i / (n - 1)) * W;
        const isIn = chart[i - 1].state === 1;
        return (
          <rect key={i} x={x1} y={isIn ? 0 : H * 0.6}
            width={x2 - x1 + 0.5}
            height={isIn ? H : H * 0.4}
            fill={isIn ? "#ef4444" : "#22c55e"}
            opacity={isIn ? 0.8 : 0.5}
          />
        );
      })}
      {/* Legende */}
      <rect x={0} y={H - 6} width={8} height={4} fill="#22c55e" opacity={0.7} />
      <text x={10} y={H - 2} fill="#475569" fontSize="6" fontFamily="sans-serif">Verfügbar</text>
      <rect x={50} y={H - 6} width={8} height={4} fill="#ef4444" opacity={0.7} />
      <text x={62} y={H - 2} fill="#475569" fontSize="6" fontFamily="sans-serif">Eingezogen</text>
    </svg>
  );
}

export default function SimulationTab({ sensors, dateFrom, dateTo, hours }) {
  const [triggerKmh, setTriggerKmh] = useState(30);
  const [hysteresisKmh, setHysteresisKmh] = useState(20);
  const [lockoutMin, setLockoutMin] = useState(15);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleSensors, setVisibleSensors] = useState(() => {
    const v = {};
    sensors.forEach(s => { v[s.name] = true; });
    return v;
  });

  const activeSensors = sensors.filter(s => s.is_active !== false);

  const getDateRange = useCallback(() => {
    if (dateFrom && dateTo) {
      return { from: new Date(dateFrom).toISOString(), to: new Date(dateTo).toISOString() };
    }
    return {
      from: new Date(Date.now() - (hours || 24) * 3600000).toISOString(),
      to: new Date().toISOString(),
    };
  }, [dateFrom, dateTo, hours]);

  const runSimulation = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      const r = await api.get(
        `/measurements/simulate?date_from=${from}&date_to=${to}` +
        `&trigger_kmh=${triggerKmh}&hysteresis_kmh=${hysteresisKmh}&lockout_minutes=${lockoutMin}`
      );
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, triggerKmh, hysteresisKmh, lockoutMin]);

  const toggleSensor = (name) => setVisibleSensors(v => ({ ...v, [name]: !v[name] }));

  return (
    <div style={S.page}>

      {/* ── Erklärung ── */}
      <div style={S.infoBox}>
        <div style={S.infoTitle}>🎛️ Storen-Regelungssimulation</div>
        <div style={S.infoText}>
          Simuliert rückwirkend anhand der Messdaten, wann die Storen ein- und ausgefahren worden wären.
          Ziel: Optimale Parameter für die spätere automatische Steuerung finden –
          wenig Schaltzyklen, maximale Nutzungszeit, kein Schaden durch Windspitzen.
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8 }}>
          <div style={S.ruleBox}>
            <span style={{ color: "#ef4444", fontWeight: 700 }}>EINFAHREN</span> wenn Böe ≥ Einfahrschwelle
          </div>
          <div style={S.ruleBox}>
            <span style={{ color: "#22c55e", fontWeight: 700 }}>AUSFAHREN</span> wenn Wind &lt; Hysterese
            <span style={{ color: "#94a3b8" }}> UND </span>Sperrzeit abgelaufen
          </div>
        </div>
      </div>

      {/* ── Parameter ── */}
      <div style={S.paramCard}>
        <div style={S.paramTitle}>Regelungsparameter</div>
        <div style={S.paramRow}>

          {/* Einfahrschwelle */}
          <div style={S.paramGroup}>
            <label style={S.paramLabel}>
              🔴 Einfahrschwelle (Böe)
              <span style={S.paramUnit}>{triggerKmh} km/h · {(triggerKmh / 3.6).toFixed(1)} m/s</span>
            </label>
            <input type="range" min="15" max="60" step="5" value={triggerKmh}
              onChange={e => setTriggerKmh(Number(e.target.value))}
              style={S.slider} />
            <div style={S.sliderMarks}>
              {[15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map(v => (
                <span key={v} style={{ ...S.sliderMark, color: v === triggerKmh ? "#ef4444" : "#334155" }}>{v}</span>
              ))}
            </div>
            <div style={S.paramHint}>
              Schenker AS150: Warnung ab 30 km/h · Zwangseinfahrt ab 50 km/h
            </div>
          </div>

          {/* Hysterese */}
          <div style={S.paramGroup}>
            <label style={S.paramLabel}>
              🟢 Ausfahren wenn Wind unter
              <span style={S.paramUnit}>{hysteresisKmh} km/h · {(hysteresisKmh / 3.6).toFixed(1)} m/s</span>
            </label>
            <input type="range" min="10" max={triggerKmh - 5} step="5" value={hysteresisKmh}
              onChange={e => setHysteresisKmh(Number(e.target.value))}
              style={S.slider} />
            <div style={S.paramHint}>
              Muss tiefer als Einfahrschwelle sein (Hysterese verhindert Flattern)
            </div>
          </div>

          {/* Sperrzeit */}
          <div style={S.paramGroup}>
            <label style={S.paramLabel}>
              ⏱️ Sperrzeit nach Einfahren
              <span style={S.paramUnit}>{lockoutMin} Minuten</span>
            </label>
            <input type="range" min="0" max="60" step="5" value={lockoutMin}
              onChange={e => setLockoutMin(Number(e.target.value))}
              style={S.slider} />
            <div style={S.sliderMarks}>
              {[0, 5, 10, 15, 20, 30, 45, 60].map(v => (
                <span key={v} style={{ ...S.sliderMark, color: v === lockoutMin ? "#3b82f6" : "#334155" }}>{v}</span>
              ))}
            </div>
            <div style={S.paramHint}>
              Mindestwartezeit bevor Ausfahren erlaubt – verhindert dauerndes Schalten
            </div>
          </div>

        </div>
        <button onClick={runSimulation} disabled={loading} style={S.runBtn}>
          {loading ? "Simuliere…" : "▶ Simulation starten"}
        </button>
      </div>

      {/* ── Sensor-Legende ── */}
      <div style={S.legendRow}>
        {activeSensors.map((s, si) => (
          <button key={s.id} onClick={() => toggleSensor(s.name)}
            style={{ ...S.legendBtn, opacity: visibleSensors[s.name] ? 1 : 0.35, borderColor: SENSOR_COLORS[si % SENSOR_COLORS.length] }}>
            <span style={{ ...S.legendDot, background: SENSOR_COLORS[si % SENSOR_COLORS.length] }} />
            {s.name}
            {sensorLage(s) && <span style={S.legendSub}>{sensorLage(s)}</span>}
          </button>
        ))}
      </div>

      {/* ── Ergebnisse ── */}
      {data && (
        <div style={S.card}>
          <div style={S.cardHeader}>
            <span style={S.cardTitle}>Simulationsergebnis</span>
            <span style={S.cardHint}>
              Einfahren bei Böe ≥ {triggerKmh} km/h · Ausfahren &lt; {hysteresisKmh} km/h · Sperrzeit {lockoutMin} Min.
            </span>
          </div>

          {/* Zusammenfassung */}
          <div style={S.summaryRow}>
            {(() => {
              const visible = activeSensors.filter(s => visibleSensors[s.name] && data[s.name]?.count > 0);
              if (visible.length === 0) return null;
              const avgCycles = Math.round(visible.reduce((sum, s) => sum + (data[s.name]?.cycles || 0), 0) / visible.length);
              const avgAvail = (visible.reduce((sum, s) => sum + (data[s.name]?.available_pct || 0), 0) / visible.length).toFixed(1);
              const avgRetracted = fmtMin(visible.reduce((sum, s) => sum + (data[s.name]?.total_retracted_min || 0), 0) / visible.length);
              return (
                <>
                  <div style={S.summaryBox}>
                    <div style={{ color: "#94a3b8", fontSize: 11 }}>Ø Schaltzyklen</div>
                    <div style={{ color: "#f97316", fontSize: 22, fontWeight: 800 }}>{avgCycles}</div>
                    <div style={{ color: "#475569", fontSize: 10 }}>Einfahrvorgänge</div>
                  </div>
                  <div style={S.summaryBox}>
                    <div style={{ color: "#94a3b8", fontSize: 11 }}>Ø Verfügbarkeit</div>
                    <div style={{ color: "#22c55e", fontSize: 22, fontWeight: 800 }}>{avgAvail}%</div>
                    <div style={{ color: "#475569", fontSize: 10 }}>Store nutzbar</div>
                  </div>
                  <div style={S.summaryBox}>
                    <div style={{ color: "#94a3b8", fontSize: 11 }}>Ø Zeit eingezogen</div>
                    <div style={{ color: "#ef4444", fontSize: 22, fontWeight: 800 }}>{avgRetracted}</div>
                    <div style={{ color: "#475569", fontSize: 10 }}>Schutzstellung</div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Detailtabelle */}
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Sensor</th>
                  <th style={S.th}>Position</th>
                  <th style={{ ...S.th, color: "#f97316" }}>Schaltzyklen</th>
                  <th style={{ ...S.th, color: "#ef4444" }}>Eingezogen</th>
                  <th style={{ ...S.th, color: "#22c55e" }}>Verfügbar</th>
                  <th style={{ ...S.th, color: "#22c55e" }}>Verfügbar %</th>
                  <th style={{ ...S.th, color: "#3b82f6" }}>Verhindert durch Sperrzeit</th>
                  <th style={S.th}>Zeitverlauf</th>
                </tr>
              </thead>
              <tbody>
                {activeSensors.map((s, si) => {
                  if (!visibleSensors[s.name]) return null;
                  const d = data[s.name];
                  if (!d || d.count === 0) return (
                    <tr key={s.id} style={{ background: si % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                      <td style={S.td}>
                        <span style={{ ...S.dot, background: SENSOR_COLORS[si % SENSOR_COLORS.length] }} />
                        <strong>{s.name}</strong>
                      </td>
                      <td style={{ ...S.td, color: "#475569" }} colSpan={7}>Keine Daten</td>
                    </tr>
                  );
                  return (
                    <tr key={s.id} style={{ background: si % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                      <td style={S.td}>
                        <span style={{ ...S.dot, background: SENSOR_COLORS[si % SENSOR_COLORS.length] }} />
                        <strong>{s.name}</strong>
                      </td>
                      <td style={{ ...S.td, color: "#64748b", fontSize: 11 }}>{sensorLage(s) || "–"}</td>
                      <td style={{ ...S.td, color: "#f97316", fontWeight: 700, fontSize: 16 }}>
                        {d.cycles}
                      </td>
                      <td style={{ ...S.td, color: "#ef4444" }}>
                        {fmtMin(d.total_retracted_min)}
                        <div style={{ color: "#475569", fontSize: 10 }}>
                          ({fmt(d.total_retracted_h, 1)} h)
                        </div>
                      </td>
                      <td style={{ ...S.td, color: "#22c55e" }}>
                        {fmtMin(d.available_min)}
                      </td>
                      <td style={S.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ width: 50, height: 8, background: "#1e3a5f", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ width: `${Math.min(d.available_pct, 100)}%`, height: "100%", background: "#22c55e", borderRadius: 4 }} />
                          </div>
                          <span style={{ color: "#22c55e", fontWeight: 700, fontSize: 13 }}>{d.available_pct}%</span>
                        </div>
                      </td>
                      <td style={{ ...S.td, color: "#3b82f6" }}>
                        {d.prevented_by_lockout}×
                        <div style={{ color: "#475569", fontSize: 10 }}>Ausfahren verzögert</div>
                      </td>
                      <td style={{ ...S.td, minWidth: 160 }}>
                        <StatusTimeline chart={d.chart} color={SENSOR_COLORS[si % SENSOR_COLORS.length]} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ color: "#334155", fontSize: 11, marginTop: 8 }}>
            * Simulation basiert auf ~{Math.round(Object.values(data).reduce((s, d) => s + (d.count || 0), 0) / activeSensors.length)} Messungen pro Sensor.
            Böen-Wert = Maximum der letzten ~10 Min. (Ecowitt). Reale Steuerung reagiert schneller auf Echtzeit-Messungen.
          </div>
        </div>
      )}

      {!data && !loading && (
        <div style={S.noData}>
          Parameter einstellen und Simulation starten →
        </div>
      )}
    </div>
  );
}

const S = {
  page: { display: "flex", flexDirection: "column", gap: 16 },
  infoBox: { background: "#0f1e35", borderRadius: 10, padding: "14px 18px", border: "1px solid #1e3a5f" },
  infoTitle: { color: "#e2e8f0", fontWeight: 700, fontSize: 15, marginBottom: 6 },
  infoText: { color: "#94a3b8", fontSize: 13, lineHeight: 1.5 },
  ruleBox: { background: "#1e293b", borderRadius: 6, padding: "6px 12px", fontSize: 12, color: "#94a3b8" },
  paramCard: { background: "#1e293b", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", gap: 16 },
  paramTitle: { color: "#f1f5f9", fontWeight: 700, fontSize: 15 },
  paramRow: { display: "flex", gap: 24, flexWrap: "wrap" },
  paramGroup: { flex: 1, minWidth: 220, display: "flex", flexDirection: "column", gap: 6 },
  paramLabel: { color: "#94a3b8", fontSize: 12, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" },
  paramUnit: { color: "#e2e8f0", fontWeight: 700, fontSize: 13 },
  paramHint: { color: "#334155", fontSize: 11 },
  slider: { width: "100%", accentColor: "#3b82f6" },
  sliderMarks: { display: "flex", justifyContent: "space-between" },
  sliderMark: { fontSize: 9, fontFamily: "monospace" },
  runBtn: { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, cursor: "pointer", fontWeight: 700, alignSelf: "flex-start" },
  legendRow: { display: "flex", gap: 6, flexWrap: "wrap" },
  legendBtn: { background: "transparent", border: "1px solid", borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", color: "#c0d0e8", display: "flex", alignItems: "center", gap: 4 },
  legendDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  legendSub: { color: "#475569", fontSize: 10, marginLeft: 2 },
  card: { background: "#1e293b", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", gap: 16 },
  cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 },
  cardTitle: { color: "#f1f5f9", fontSize: 15, fontWeight: 700 },
  cardHint: { color: "#475569", fontSize: 12 },
  summaryRow: { display: "flex", gap: 16, flexWrap: "wrap" },
  summaryBox: { background: "#0f172a", borderRadius: 10, padding: "12px 20px", minWidth: 120, textAlign: "center" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 700 },
  th: { color: "#64748b", fontSize: 12, fontWeight: 600, textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #334155", whiteSpace: "nowrap" },
  td: { color: "#e2e8f0", fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #1e3a5f", whiteSpace: "nowrap" },
  dot: { display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 6 },
  noData: { color: "#475569", textAlign: "center", marginTop: 40, fontSize: 15 },
};
